import axios from "axios";
import { CreateTaskModel, UpdateTaskStatusModel, UpdateTaskDueDateModel, UpdateTaskModel } from "../interfaces/Models/TaskModel";

// GetMyTasks, GetTask, UpdateTaskDueDate and DeleteTask only have the default [controller]/[action] routes in TaskController
export const getMyTasks = (projectId?: number) => {
    const params = projectId ? { projectId } : {};
    return axios.get("/Task/GetMyTasks", { params });
}

export const getTaskById = (taskId: number) => {
    return axios.get(`/Task/GetTask?taskId=${taskId}`);
}

export const createTask = (task: CreateTaskModel) => {
    return axios.post("/task", task);
}

export const createTaskWithImages = (task: CreateTaskModel, images: File[]) => {
    const formData = new FormData();
    
    // Add task data
    formData.append("Description", task.description);
    
    // Send the date-input value (YYYY-MM-DD) unchanged, like the JSON create does.
    // Converting local midnight with toISOString() stored the previous day (UTC) on the server.
    // Fallback: today's local date as YYYY-MM-DD.
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const dueDateString = task.dueDate && task.dueDate.trim() !== '' ? task.dueDate : todayString;
    formData.append("DueDate", dueDateString);
    
    formData.append("Priority", task.priority.toString());
    formData.append("AssignedToUserId", task.assignedToUserId);
    formData.append("ProjectId", task.projectId.toString());
    
    if (task.checklistDefinitionId) {
        formData.append("ChecklistDefinitionId", task.checklistDefinitionId.toString());
    }
    if (task.checklistPositionId) {
        formData.append("ChecklistPositionId", task.checklistPositionId.toString());
    }
    
    // Add images
    images.forEach((image) => {
        formData.append("images", image);
    });

    return axios.post("/task/with-images", formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
}

export const updateTaskStatus = (taskId: number, model: UpdateTaskStatusModel) => {
    return axios.put(`/task/${taskId}/status`, model);
}

export const updateTaskDueDate = (taskId: number, model: UpdateTaskDueDateModel) => {
    return axios.put("/Task/UpdateTaskDueDate", { TaskId: taskId, NewDueDate: model.dueDate });
}

export const updateTask = (taskId: number, model: UpdateTaskModel) => {
    return axios.put(`/task/${taskId}`, model);
}

export const deleteTask = (taskId: number) => {
    return axios.delete(`/Task/DeleteTask?taskId=${taskId}`);
}

export const addTaskImage = (taskId: number, formData: FormData) => {
    return axios.post(`/Task/AddImageToTask?taskId=${taskId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
}

export const generateTasksFromChecklist = (checklistDefinitionId: number, dueDate: string) => {
    return axios.post("/task/generate-from-checklist", {
        checklistDefinitionId,
        dueDate
    });
}

export const getAllTasks = (orderBy: boolean, orderColumn: string, toTake: number, toSkip: number, projectId?: number, status?: number) => {
    const params: any = { orderBy, orderColumn, toTake, toSkip };
    if (projectId) params.projectId = projectId;
    if (status !== undefined) params.status = status;
    return axios.get("/task", { params });
}

export const getTaskCount = (projectId?: number, status?: number) => {
    const params: any = {};
    if (projectId) params.projectId = projectId;
    if (status !== undefined) params.status = status;
    return axios.get("/task/count", { params });
}

export const createSubTask = (taskId: number, description: string) => {
    return axios.post(`/task/${taskId}/subtasks`, { description });
}

export const createSubTaskWithImages = (taskId: number, description: string, images: File[]) => {
    const formData = new FormData();
    formData.append("description", description);
    images.forEach(image => formData.append("images", image));

    return axios.post(`/task/${taskId}/subtasks/with-images`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
}

export const getSubTasks = (taskId: number) => {
    return axios.get(`/task/${taskId}/subtasks`);
}

export const updateSubTask = (subTaskId: number, description: string) => {
    return axios.put(`/task/subtasks/${subTaskId}`, { description });
}

export const updateSubTaskDone = (subTaskId: number, isDone: boolean) => {
    return axios.patch(`/task/subtasks/${subTaskId}/done`, { isDone });
}

export const deleteSubTask = (subTaskId: number) => {
    return axios.delete(`/task/subtasks/${subTaskId}`);
}
